import { can, formatCode, WRITE_OFF_REASONS } from '@pl/domain';
import type { CountBoxDto, CountDto, CountHiddenDto, CountResultDto, DiscrepancyDto, WriteOffReason } from '@pl/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../lib/api';
import { stateWords } from '../../lib/format';
import { qk, useCountProgress, useCounts, useDiscrepancies, useMe, useSiteMap, useSkuMap } from '../../lib/queries';
import { Empty, ErrorNote, RefetchNote, SkuTag, Spinner } from '../../ui/bits';
import { useScanSite } from '../useScanSite';
import { countSignalAt, OPEN_POLL_MS, openCountState } from './countKit';
import { kit, ScanSheet } from './scanKit';
import s from './CountResult.module.css';

type Resolve = { resolution: 'written_off'; reason: WriteOffReason; note?: string } | { resolution: 'left' } | { resolution: 'found' };

const REASON_LABEL: Record<WriteOffReason, string> = { damaged: 'Damaged', lost: 'Lost', sample: 'Sample', other: 'Other' };
const RESOLVED: Record<NonNullable<DiscrepancyDto['resolution']>, string> = {
  written_off: 'Written off',
  left: 'Left for a recount',
  found: 'Found here',
  received: 'Received on its transfer',
  shipped: 'Shipped on its order',
};

/** "8:40 AM", on the site's wall clock. */
const clock = (iso: string, timeZone: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone });

/**
 * What a finished count found, and what a lead does about it.
 *
 * Every missing or unexpected box is its own discrepancy. A lead resolves each one here — a missing
 * box is written off with a reason or left for a recount; an unexpected one is marked found here —
 * and the row changes to say what was done, from the server's own record, not the phone's memory.
 * A worker sees the same lists with no buttons: deciding what a missing box means is a lead's call.
 */
export function CountResult() {
  const { countId = '' } = useParams();
  const me = useMe();
  const { site } = useScanSite();
  const qc = useQueryClient();
  const skuById = useSkuMap();
  const siteById = useSiteMap();
  // The server decides: on the scan surface it answers `resultHidden` while any count is open at the
  // count's site, whoever asks — the one who finished it too. This screen draws only what it was
  // answered after it opened, never what the phone has cached: every earlier leak was a stale cache
  // drawn or trusted. The open counts at the result's own site are a second wall: while the phone
  // knows one is open there, or cannot tell, it does not ask. The site comes from the count's
  // progress, which never carries a result.
  const progress = useCountProgress(countId);
  const resultSite = progress.data?.siteId;
  const siteName = siteById.get(resultSite ?? '')?.name ?? 'this site';
  // The result's own site, every 10 s: the phone streams only its own site, and a stream can die unheard (I-1).
  const opened = useCounts({ siteId: resultSite, status: 'open', limit: 100 }, Boolean(resultSite), OPEN_POLL_MS);
  const openHere = openCountState(opened, resultSite);
  // A canceled count has no result, and its progress says so: nothing more is asked for.
  const cancelled = progress.data?.status === 'cancelled' ? progress.data : null;
  // Its own question, per visit, never joined to one an earlier visit left on its way, and each
  // answer stamped with when it was asked. Only an answer to a request made after this screen opened
  // and after the latest count signal for the site is drawn (N10); a signal takes the answer away
  // (`useLive`), and the screen asks again.
  const [openedAt] = useState(() => Date.now());
  const [visit] = useState(() => `${openedAt}-${Math.random().toString(36).slice(2)}`);
  const query = useQuery({
    queryKey: [...qk.count(countId), 'asked', visit],
    queryFn: async () => {
      const at = Date.now();
      return { at, dto: await api.get<CountDto | CountResultDto | CountHiddenDto>(`/counts/${countId}`) };
    },
    enabled: Boolean(countId) && !cancelled && openHere === 'none',
    gcTime: 0,
  });
  const signal = countSignalAt(resultSite);
  const c = query.data && query.data.at >= openedAt && (signal === undefined || query.data.at > signal) ? query.data.dto : undefined;
  const hidden = Boolean(c && 'resultHidden' in c);
  const finished = c && !hidden && c.status === 'finished' && 'missingBoxes' in c ? (c as CountResultDto) : null;
  // This count's own, by its id: a page of the site's could leave this count's rows off it, and a
  // row read as open when it is resolved offers buttons the server then refuses. Asked for only
  // beside a result this screen was answered.
  const discrepancies = useDiscrepancies({ sourceId: countId, limit: 200 }, Boolean(finished));
  const noteId = useId();

  const [writeOff, setWriteOff] = useState<CountBoxDto | null>(null);
  const [reason, setReason] = useState<WriteOffReason | null>(null);
  const [note, setNote] = useState('');
  const keepIt = useRef<HTMLButtonElement>(null);

  const byId = useMemo(() => new Map((discrepancies.data ?? []).map((d) => [d.id, d])), [discrepancies.data]);

  const resolve = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Resolve }) => api.post<DiscrepancyDto>(`/discrepancies/${id}/resolve`, body),
    onSuccess: () => {
      closeWriteOff();
      // The row changes because the server says it did, so both are asked for again.
      void qc.invalidateQueries({ queryKey: ['discrepancies'] });
      void qc.invalidateQueries({ queryKey: qk.count(countId) });
    },
  });

  function closeWriteOff() {
    setWriteOff(null);
    setReason(null);
    setNote('');
  }

  if (cancelled) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty
          title="This count was canceled"
          description={cancelled.cancelledBy === 'end_of_day' ? 'It was not finished by the end of the day. Nothing was recorded.' : 'It was canceled, and nothing was recorded.'}
          action={
            <Link to="/scan/count" className="bigbtn ghost">
              Back to counts
            </Link>
          }
        />
      </div>
    );
  }
  // Hidden because the phone knows a count is open at the site, or because the server said so.
  if (openHere === 'open' || hidden) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty
          title={`Hidden while a count is open at ${siteName}`}
          description="The office can see it."
          action={
            <Link to="/scan/count" className="bigbtn ghost">
              Back to counts
            </Link>
          }
        />
      </div>
    );
  }
  if (openHere === 'unknown') {
    const failed = progress.isLoadingError ? progress : opened.isLoadingError ? opened : null;
    if (failed) {
      return (
        <div className={`scan-body ${kit.tkCentre}`}>
          <div className={kit.tkStack}>
            <ErrorNote error={failed.error} />
            <button type="button" className={`bigbtn ghost ${kit.tkRetry}`} onClick={() => void failed.refetch()}>
              Try again
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Spinner label="Checking for counts in progress" />
      </div>
    );
  }
  // Until this screen's own answer arrives it draws nothing, even with an answer in the cache.
  if (!c && !query.isError) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Spinner label="Loading the result" />
      </div>
    );
  }
  if (!c) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <div className={kit.tkStack}>
          <ErrorNote error={query.error} />
          <button type="button" className={`bigbtn ghost ${kit.tkRetry}`} onClick={() => void query.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  // An open count has no result, and this screen shows nothing in its place: it only sends the
  // worker back to finish it.
  if (!finished) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty
          title="This count isn’t finished"
          description="The result shows once the count is finished."
          action={
            <Link to={`/scan/count/${c.id}`} className="bigbtn ghost">
              Back to the count
            </Link>
          }
        />
      </div>
    );
  }

  const r = finished.result!;
  const lead = Boolean(me.data && can(me.data.role, 'resolve_discrepancy'));
  const tz = site?.timezone ?? siteById.get(finished.siteId)?.timezone ?? 'UTC';
  const sku = finished.skuId ? skuById.get(finished.skuId) : undefined;
  const anything = finished.missingBoxes.length + finished.unexpectedBoxes.length > 0;
  const needNote = reason === 'other' && note.trim().length === 0;

  /** Where an unexpected box was on the record when it turned up here. */
  const recorded = (box: CountBoxDto) => {
    if ((box.state === 'in_stock' || box.state === 'picked') && box.siteId && box.siteId !== finished.siteId)
      return `Recorded at ${siteById.get(box.siteId)?.name ?? 'another site'}`;
    if (box.state === 'labelled') return 'Never received';
    return `Recorded as ${stateWords(box.state).toLowerCase()}`;
  };

  const row = (box: CountBoxDto, kind: 'missing' | 'unexpected') => {
    const d = box.discrepancyId ? byId.get(box.discrepancyId) : undefined;
    const open = Boolean(box.discrepancyId) && (d?.status ?? 'open') === 'open';
    const status =
      d?.status === 'resolved' && d.resolution
        ? d.resolution === 'written_off' && d.writeOffReason
          ? `${RESOLVED.written_off} · ${REASON_LABEL[d.writeOffReason]}`
          : RESOLVED[d.resolution]
        : kind === 'missing'
          ? 'Not scanned'
          : recorded(box);
    const mine = resolve.isPending && resolve.variables?.id === box.discrepancyId;
    return (
      <li key={box.code} className={`${s.boxRow}${open ? '' : ` ${s.boxDone}`}`} aria-label={formatCode(box.code)}>
        <div className={s.boxTop}>
          <span className={`skux ${s.boxSku}`}>
            <SkuTag sku={skuById.get(box.skuId ?? '')} short />
          </span>
          <span className={`mono ${s.boxCode}`}>{formatCode(box.code)}</span>
        </div>
        <p className={s.boxStatus}>{status}</p>
        {/* A picked box is on an order: the server refuses to write it off, so it is not offered. */}
        {open && kind === 'missing' && box.state === 'picked' && <p className={s.boxWhy}>Picked for an order — resolve from the order</p>}
        {lead && open && (
          <div className={s.boxActions}>
            {kind === 'missing' ? (
              <>
                {box.state !== 'picked' && (
                  <button type="button" className={`bigbtn ghost ${s.actBtn}`} disabled={resolve.isPending} onClick={() => setWriteOff(box)}>
                    Write off
                  </button>
                )}
                <button
                  type="button"
                  className={`bigbtn ghost ${s.actBtn}`}
                  disabled={resolve.isPending}
                  onClick={() => resolve.mutate({ id: box.discrepancyId!, body: { resolution: 'left' } })}
                >
                  Recount later
                </button>
              </>
            ) : (
              <button
                type="button"
                className={`bigbtn ghost ${s.actBtn}`}
                disabled={resolve.isPending}
                onClick={() => resolve.mutate({ id: box.discrepancyId!, body: { resolution: 'found' } })}
              >
                Found here
              </button>
            )}
          </div>
        )}
        {mine && <Spinner label="Saving" />}
        {resolve.isError && resolve.variables?.id === box.discrepancyId && !writeOff && <ErrorNote error={resolve.error} />}
      </li>
    );
  };

  return (
    <>
      <div className={`scan-body ${s.resultBody}`}>
        <RefetchNote queries={[query, discrepancies]} />
        <div className={s.what}>
          {finished.scope === 'pallet' ? (
            <b className={`mono ${s.whatPallet}`}>{`Pallet ${formatCode(finished.palletCode ?? '')}`}</b>
          ) : (
            <span className={`skux ${s.whatSku}`}>
              <SkuTag sku={sku} withCode />
            </span>
          )}
          <p className={s.whatWhen}>{`Finished ${finished.finishedAt ? clock(finished.finishedAt, tz) : ''} · counted by ${finished.startedByName}`}</p>
        </div>

        <ul className={s.strip} aria-label="Count result">
          <li>
            <span>Expected</span>
            <b>{r.expected}</b>
          </li>
          <li>
            <span>Found</span>
            <b>{r.found}</b>
          </li>
          <li className={r.missing > 0 ? s.stripBad : undefined}>
            <span>Missing</span>
            <b>{r.missing}</b>
          </li>
          <li className={r.unexpected > 0 ? s.stripWarn : undefined}>
            <span>Unexpected</span>
            <b>{r.unexpected}</b>
          </li>
        </ul>

        {!anything && <Empty title="Everything expected was found" description="No boxes need a lead’s attention." />}

        {finished.missingBoxes.length > 0 && (
          <>
            <div className="sect">
              Missing boxes<span className="r">{lead ? 'Lead decides' : `${finished.missingBoxes.length}`}</span>
            </div>
            <ul className={s.boxList} aria-label="Missing boxes">
              {finished.missingBoxes.map((box) => row(box, 'missing'))}
            </ul>
          </>
        )}

        {finished.unexpectedBoxes.length > 0 && (
          <>
            <div className="sect">
              Unexpected boxes<span className="r">{lead ? 'Found here puts it in stock' : `${finished.unexpectedBoxes.length}`}</span>
            </div>
            <ul className={s.boxList} aria-label="Unexpected boxes">
              {finished.unexpectedBoxes.map((box) => row(box, 'unexpected'))}
            </ul>
          </>
        )}

        {anything && !lead && <p className={s.leadNote}>A lead will review these.</p>}
        {anything && lead && (
          <p className={s.leadNote}>
            {`Write off asks for a reason.${finished.unexpectedBoxes.length > 0 ? ` Found here puts the box back in stock at ${siteName}.` : ''}`}
          </p>
        )}
      </div>

      <div className="scan-foot">
        <Link to="/scan/count" className={`bigbtn ghost ${s.backLink}`}>
          Back to counts
        </Link>
      </div>

      <ScanSheet
        open={writeOff !== null}
        onClose={() => {
          closeWriteOff();
          resolve.reset();
        }}
        busy={resolve.isPending}
        safeFocus={keepIt}
        title={`Write off ${formatCode(writeOff?.code ?? '')}?`}
        lead={`The box comes off the record at ${siteName}. Choose why.`}
        actions={
          <>
            <ErrorNote error={resolve.error} />
            <button
              type="button"
              className={`bigbtn ${kit.tkDanger}`}
              disabled={resolve.isPending || reason === null || needNote}
              onClick={() =>
                resolve.mutate({
                  id: writeOff!.discrepancyId!,
                  body: { resolution: 'written_off', reason: reason!, ...(note.trim() ? { note: note.trim() } : {}) },
                })
              }
            >
              Write off
            </button>
            <button type="button" ref={keepIt} className="bigbtn ghost" disabled={resolve.isPending} onClick={closeWriteOff}>
              Keep it
            </button>
          </>
        }
      >
        <fieldset className={s.reasons}>
          <legend className={kit.tkCap}>Reason</legend>
          <div className={s.segs}>
            {WRITE_OFF_REASONS.map((value) => (
              <label key={value} className={`${s.seg}${reason === value ? ` ${s.segOn}` : ''}`}>
                <input type="radio" name="write-off-reason" value={value} checked={reason === value} onChange={() => setReason(value)} />
                {REASON_LABEL[value]}
              </label>
            ))}
          </div>
        </fieldset>
        <label className={kit.tkCap} htmlFor={noteId}>
          {reason === 'other' ? 'Note (needed for Other)' : 'Note'}
        </label>
        <textarea id={noteId} className={kit.tkReason} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2} placeholder="Optional" />
      </ScanSheet>
    </>
  );
}
